import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { announcementFormSchema, type AnnouncementFormData } from '../schemas';
import { AnnouncementPriority, AnnouncementStatus, type AnnouncementDto } from '../types';
import { MarkdownEditor } from './MarkdownEditor';

interface AnnouncementFormProps {
  initial?: AnnouncementDto;
  isSubmitting?: boolean;
  onSubmit: (data: AnnouncementFormData) => void | Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  /**
   * Called whenever the form's dirty state flips. Lets the parent dialog
   * prompt for unsaved changes before closing.
   */
  onDirtyChange?: (isDirty: boolean) => void;
}

function toLocalInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  // datetime-local expects YYYY-MM-DDTHH:mm in local time.
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(local: string): string | null {
  if (!local) return null;
  return new Date(local).toISOString();
}

export function AnnouncementForm({
  initial,
  isSubmitting,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  onDirtyChange,
}: AnnouncementFormProps) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<AnnouncementFormData>({
    resolver: zodResolver(announcementFormSchema),
    defaultValues: {
      title: initial?.title ?? '',
      content: initial?.content ?? '',
      priority: initial?.priority ?? AnnouncementPriority.Normal,
      status: initial?.status ?? AnnouncementStatus.Draft,
      isPinned: initial?.isPinned ?? false,
      publishAt: initial?.publishAt ?? null,
      expiresAt: initial?.expiresAt ?? null,
    },
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-1">
        <Label htmlFor="title">Title</Label>
        <Input id="title" {...register('title')} />
        {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
      </div>

      <div className="space-y-1">
        <Label htmlFor="content">Content (Markdown)</Label>
        <Controller
          name="content"
          control={control}
          render={({ field }) => (
            <MarkdownEditor id="content" value={field.value} onChange={field.onChange} />
          )}
        />
        {errors.content && <p className="text-xs text-destructive">{errors.content.message}</p>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Priority</Label>
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <Select
                value={String(field.value)}
                onValueChange={(v) => field.onChange(Number(v) as AnnouncementPriority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(AnnouncementPriority.Normal)}>Normal</SelectItem>
                  <SelectItem value={String(AnnouncementPriority.Important)}>Important</SelectItem>
                  <SelectItem value={String(AnnouncementPriority.Critical)}>Critical</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-1">
          <Label>Status</Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                value={String(field.value)}
                onValueChange={(v) => field.onChange(Number(v) as AnnouncementStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(AnnouncementStatus.Draft)}>Draft</SelectItem>
                  <SelectItem value={String(AnnouncementStatus.Published)}>Published</SelectItem>
                  <SelectItem value={String(AnnouncementStatus.Archived)}>Archived</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="publishAt">Publish at (optional)</Label>
          <Controller
            name="publishAt"
            control={control}
            render={({ field }) => (
              <Input
                id="publishAt"
                type="datetime-local"
                value={toLocalInputValue(field.value)}
                onChange={(e) => field.onChange(fromLocalInputValue(e.target.value))}
              />
            )}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="expiresAt">Expires at (optional)</Label>
          <Controller
            name="expiresAt"
            control={control}
            render={({ field }) => (
              <Input
                id="expiresAt"
                type="datetime-local"
                value={toLocalInputValue(field.value)}
                onChange={(e) => field.onChange(fromLocalInputValue(e.target.value))}
              />
            )}
          />
          {errors.expiresAt && (
            <p className="text-xs text-destructive">{errors.expiresAt.message}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Controller
          name="isPinned"
          control={control}
          render={({ field }) => (
            <Switch id="isPinned" checked={field.value} onCheckedChange={field.onChange} />
          )}
        />
        <Label htmlFor="isPinned" className="cursor-pointer">
          Pin to top
        </Label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
